import { normalizeEssayStatusKey } from './essayStatusPriority'

export const NEEDS_ATTENTION_FILTER_OPTIONS = [
	{ value: 'new_application', label: 'New application' },
	{ value: 'new_feedback', label: 'New feedback' },
	{ value: 'new_documents', label: 'New documents' },
	{ value: 'essay_pending_review', label: 'Essay pending review' },
]

export const ESSAY_STATUS_FILTER_OPTIONS = [
	{
		value: 'pending_review',
		label: 'Pending review',
		statuses: ['submitted_for_review'],
	},
	{
		value: 'revision_requested',
		label: 'Revision requested',
		statuses: ['revision_requested'],
	},
	{ value: 'draft', label: 'Draft', statuses: ['draft', 'draft_only'] },
	{ value: 'approved', label: 'Approved', statuses: ['approved', 'final'] },
	{ value: 'no_essay', label: 'No essay', statuses: [] },
]

export const getRowTargetId = row =>
	String(row?.applicationTargetId || row?.id || '').trim()

export const getRowEssayAggregateStatus = (row, aggregateMap) => {
	const targetId = getRowTargetId(row)
	const fromSummary =
		targetId && aggregateMap?.get ? aggregateMap.get(targetId) : null
	const raw = fromSummary || row?.essayAggregateStatus || row?.essay
	return normalizeEssayStatusKey(raw)
}

const countRows = (rows, predicate) =>
	(rows || []).reduce((total, row) => (predicate(row) ? total + 1 : total), 0)

export const buildCountryFilterOptions = (rows = []) => {
	const counts = {}
	;(rows || []).forEach(row => {
		const code = String(row?.country || '')
			.trim()
			.toUpperCase()
		if (!code) return
		counts[code] = (counts[code] || 0) + 1
	})

	return Object.keys(counts)
		.sort()
		.map(code => ({
			value: code,
			label: code,
			count: counts[code],
		}))
}

export const buildNeedsAttentionFilterOptions = (rows = [], aggregateMap) =>
	NEEDS_ATTENTION_FILTER_OPTIONS.map(option => {
		const count = countRows(rows, row =>
			rowMatchesNeedsAttention(row, [option.value], aggregateMap),
		)
		return {
			value: option.value,
			label: option.label,
			count,
		}
	})

export const buildEssayStatusFilterOptions = (
	rows = [],
	aggregateMap,
	essaysByTargetId,
) =>
	ESSAY_STATUS_FILTER_OPTIONS.map(option => {
		const count = countRows(rows, row =>
			rowMatchesEssayStatus(
				row,
				[option.value],
				aggregateMap,
				essaysByTargetId,
			),
		)
		return {
			value: option.value,
			label: option.label,
			count,
		}
	})

export const rowMatchesNeedsAttention = (row, selected = [], aggregateMap) => {
	if (!selected?.length) return true
	return selected.some(key => {
		if (key === 'new_application') return Boolean(row?.isNewApplication)
		if (key === 'new_feedback') return Number(row?.feedbackCount) > 0
		if (key === 'new_documents') return Number(row?.newDocumentsCount) > 0
		if (key === 'essay_pending_review') {
			return (
				getRowEssayAggregateStatus(row, aggregateMap) === 'submitted_for_review'
			)
		}
		return false
	})
}

export const rowMatchesEssayStatus = (
	row,
	selected = [],
	aggregateMap,
	essaysByTargetId,
) => {
	if (!selected?.length) return true
	const status = getRowEssayAggregateStatus(row, aggregateMap)
	const targetId = getRowTargetId(row)
	const essays =
		targetId && essaysByTargetId?.get
			? essaysByTargetId.get(targetId) || []
			: []

	return selected.some(filterKey => {
		if (filterKey === 'no_essay') {
			return !status && essays.length === 0
		}
		const option = ESSAY_STATUS_FILTER_OPTIONS.find(
			item => item.value === filterKey,
		)
		if (!option?.statuses?.length) return false
		return option.statuses.includes(status)
	})
}

export const getNeedsAttentionFilterLabel = value => {
	const item = NEEDS_ATTENTION_FILTER_OPTIONS.find(
		option => option.value === value,
	)
	return item?.label || value
}

export const getEssayStatusFilterLabel = value => {
	const item = ESSAY_STATUS_FILTER_OPTIONS.find(
		option => option.value === value,
	)
	return item?.label || value
}

export const sanitizePanelFilters = (filters, allowed) => ({
	country: (filters?.country || []).filter(value => allowed.country.has(value)),
	needsAttention: (filters?.needsAttention || []).filter(value =>
		allowed.needsAttention.has(value),
	),
	essayStatus: (filters?.essayStatus || []).filter(value =>
		allowed.essayStatus.has(value),
	),
})
